/**
 * The Service Procurement dashboard.
 *
 * Ten roles reach this screen and they come for completely different reasons.
 * A site engineer wants to know what is left to measure at his site this week;
 * the CMD wants to know what the company has committed to contractors. Showing
 * both of them the same six tiles would serve neither.
 *
 * So the screen is assembled from BLOCKS, and each role gets the blocks that
 * answer its own question. Every block reads the same views, through row level
 * security, so two people looking at the same block see the same arithmetic on
 * different rows.
 *
 *   site_engineer              measure    what is left to measure at my site
 *   site_construction_manager  certify    what is waiting for my signature
 *   project_manager            portfolio  my sites, their contracts, their progress
 *   procurement_manager/head   contracts  orders in the chain, awaiting me
 *   project_head / cmd / cxo   board      committed, certified, billed
 *   accounts_spoc              money      RCM, TDS, retention - Phase 4 and 6
 *
 * Charts are deliberately few. The one thing every role asked for in different
 * words is "how far along is this contract", so that is the chart, and it is a
 * progress bar per work order rather than a doughnut - because a doughnut of
 * work orders answers no question anybody has.
 */
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  AlertTriangle, ArrowRight, CheckCircle2, FileSignature, Landmark, ListChecks,
  Ruler, TrendingUp, Wallet,
} from 'lucide-react'
import { useAuth } from '@/lib/auth'
import { useQuery } from '@/lib/data'
import { supabase } from '@/lib/supabase'
import { ddmmyyyy, rupees } from '@/lib/format'
import { ROLE_LABEL } from '@/lib/domain'
// Shared with the Materials dashboard by decision, 30-09-2026 (migration 0185).
import { SitePhotoCarousel, maySeeSitePhotos } from '@/components/site-photos/SitePhotoCarousel'
import { Card, EmptyState, Loading, Stat, StatusPill } from '@/components/ui'
import { serviceCan } from '../nav'

interface WoRow {
  id: string; site_id: string; site_name: string; vendor_name: string
  title: string; status: string; current_step: number
  contract_value: number; total_with_gst: number
  is_reverse_charge: boolean; gst_amount: number
  completion_date: string | null; days_overdue: number | null
  line_count: number; pricing_bases: string | null
  vendor_pan: string | null; tds_pct: number | null
}
interface ProgressRow {
  wo_line_id: string; wo_id: string; site_name: string; wo_title: string
  description: string; uom: string
  qty_ordered: number; qty_allowed: number; qty_certified: number
  qty_left_to_measure: number; value_ready_to_bill: number
  pct_complete: number | null
}
interface MbRow {
  id: string; wo_id: string; site_id: string; status: string
  measurement_date: string; location_ref: string | null
  recorder_email: string | null
}

const n = (v: unknown) => Number(v ?? 0) || 0

export default function ServiceDashboard() {
  const { profile } = useAuth()
  const role = profile?.role

  const { data: wos, loading: l1 } = useQuery<WoRow>(
    'v_work_order_register',
    () => supabase.from('v_work_order_register').select('*').order('date_created', { ascending: false }),
    [], { cacheable: false })

  const { data: prog, loading: l2 } = useQuery<ProgressRow>(
    'v_wo_line_progress',
    () => supabase.from('v_wo_line_progress').select('*'),
    [], { cacheable: false, idField: 'wo_line_id' })

  const { data: books } = useQuery<MbRow>(
    'wo_measurements',
    () => supabase.from('wo_measurements').select('*')
      .order('measurement_date', { ascending: false }).limit(50),
    [], { cacheable: false })

  /**
   * VARIATIONS — added 16-09-2026, with the screen that lets one be raised.
   *
   * Two numbers, not a block: what approved variations have added to the
   * contract values, and how many are sitting in the chain. The first is the
   * board's question ("what have we actually committed to, as against what we
   * signed"), the second is Procurement's.
   */
  const { data: vos } = useQuery<any>(
    'v_variation_register',
    () => supabase.from('v_variation_register').select('*'),
    [], { cacheable: false })

  // ---- the arithmetic, once ------------------------------------------------
  const m = useMemo(() => {
    const live = wos.filter((w) => !['CANCELLED', 'REJECTED', 'DRAFT'].includes(w.status))
    const inChain = wos.filter((w) => w.status === 'SUBMITTED')
    return {
      orders: live.length,
      committed: live.reduce((a, w) => a + n(w.total_with_gst), 0),
      awaitingApproval: inChain.length,
      awaitingValue: inChain.reduce((a, w) => a + n(w.total_with_gst), 0),
      overdue: wos.filter((w) => n(w.days_overdue) > 0).length,
      rcmLiability: live.filter((w) => w.is_reverse_charge)
        .reduce((a, w) => a + Math.round(n(w.contract_value) * 0.18 * 100) / 100, 0),
      rcmCount: live.filter((w) => w.is_reverse_charge).length,
      noPan: wos.filter((w) => !w.vendor_pan).length,
      readyToBill: prog.reduce((a, p) => a + n(p.value_ready_to_bill), 0),
      leftToMeasure: prog.filter((p) => n(p.qty_left_to_measure) > 0).length,
      certified: prog.reduce((a, p) => a + n(p.qty_certified) * 0, 0), // value comes via ready-to-bill
      draftBooks: books.filter((b) => b.status === 'DRAFT').length,
      submittedBooks: books.filter((b) => b.status === 'SUBMITTED').length,
      variedBy: (vos as any[]).filter((v) => v.status === 'APPROVED')
        .reduce((a2, v) => a2 + n(v.variation_value), 0),
      variationsInChain: (vos as any[]).filter((v) => v.status === 'SUBMITTED').length,
      variationDrafts: (vos as any[])
        .filter((v) => ['DRAFT', 'RETURNED'].includes(v.status)).length,
    }
  }, [wos, prog, books, vos])

  // Which blocks does this person get?
  const isSite = role === 'site_engineer' || role === 'site_construction_manager'
  const isProc = role === 'procurement_manager' || role === 'procurement_head'
  const isBoard = role === 'cmd' || role === 'cxo' || role === 'project_head'
  const isPm = role === 'project_manager'
  const isAccounts = role === 'accounts_spoc'
  const isAdmin = role === 'admin'

  if (l1 || l2) return <Loading label="Reading the contracts…" />

  const nothingYet = wos.length === 0
  // Head office and, since 0187, the Project Managers.
  const isHo = maySeeSitePhotos(profile!.role)

  return (
    <div className="space-y-5">
      {/* ---------- who you are, what this side is for ---------- */}
      <div>
        <h1 className="text-xl font-bold text-slate-900">Service Procurement</h1>
        <p className="text-sm text-slate-500">
          {ROLE_LABEL[profile!.role]}
          {isSite && ' · what is waiting at your site'}
          {isProc && ' · contracts in the chain'}
          {isPm && ' · your sites'}
          {isBoard && ' · what the company has committed to works contractors'}
          {isAccounts && ' · what is owed, deducted and due'}
        </p>
      </div>

      {nothingYet && (
        // Head office (0185): the site photos sit beside the empty state.
        <div className={isHo ? 'grid gap-3 md:grid-cols-3' : undefined}>
          <div className={isHo ? 'md:col-span-2' : undefined}>
            <EmptyState
              icon={<FileSignature className="h-8 w-8" />}
              title="No work orders yet"
              message={serviceCan(role, 'createWorkOrder')
                ? 'Raise the first one from Work Orders. Everything on this screen fills in from there.'
                : 'Nothing has been raised yet. This screen fills in as soon as Procurement issues the first work order.'}
            />
          </div>
          {isHo && <SitePhotoCarousel />}
        </div>
      )}

      {!nothingYet && (
        <>
          {/* ---------- headline tiles, chosen by role ---------- */}
          {/* Head office (30-09-2026, 0185): the site photo carousel takes
              the right third across the first two rows, and the tiles flow
              around it - two beside it, then full width beneath. Everybody
              else keeps the four-across grid unchanged. */}
          <div className={isHo
            ? 'grid gap-3 sm:grid-cols-2 md:grid-cols-3 md:[grid-auto-flow:dense]'
            : 'grid gap-3 sm:grid-cols-2 lg:grid-cols-4'}>
            {isHo && (
              <SitePhotoCarousel className="sm:col-span-2 md:col-span-1 md:col-start-3 md:row-span-2 md:row-start-1 md:self-center" />
            )}
            {/* The sub-line NAMES the variations rather than the tile adding
                them in. "Committed" is a total WITH GST and a variation's value
                is without it, so folding one into the other would be two tax
                bases in one number - which is how a board paper ends up
                defending a figure nobody can reproduce. */}
            {(isBoard || isProc || isPm || isAdmin) && (
              <Stat label="Committed to contractors" value={rupees(m.committed)}
                    sub={`${m.orders} live work order${m.orders === 1 ? '' : 's'}`
                         + (m.variedBy !== 0
                            ? ` · ${rupees(m.variedBy)} of it by variation`
                            : '')}
                    icon={<FileSignature className="h-5 w-5" />} />
            )}
            {(isProc || isBoard || isAdmin) && (
              <Stat label="Awaiting approval" value={m.awaitingApproval}
                    sub={m.awaitingApproval ? rupees(m.awaitingValue) : 'nothing in the chain'}
                    tone={m.awaitingApproval ? 'warn' : 'default'}
                    icon={<CheckCircle2 className="h-5 w-5" />} />
            )}
            {(isSite || isPm || isAdmin) && (
              <Stat label="Lines left to measure" value={m.leftToMeasure}
                    sub="across every live order you can see"
                    icon={<Ruler className="h-5 w-5" />} />
            )}
            {(isSite || isAdmin) && (
              <Stat label="Books awaiting certification" value={m.submittedBooks}
                    sub={m.draftBooks ? `${m.draftBooks} still in draft` : 'none in draft'}
                    tone={m.submittedBooks ? 'warn' : 'default'}
                    icon={<CheckCircle2 className="h-5 w-5" />} />
            )}
            {(isAccounts || isBoard || isPm || isAdmin) && (
              <Stat label="Certified, not yet billed" value={rupees(m.readyToBill)}
                    sub="work measured and signed off, no RA bill raised"
                    tone={m.readyToBill > 0 ? 'warn' : 'default'}
                    icon={<Wallet className="h-5 w-5" />} />
            )}
            {(isAccounts || isAdmin) && (
              <Stat label="Reverse charge GST" value={rupees(m.rcmLiability)}
                    sub={`${m.rcmCount} unregistered contractor${m.rcmCount === 1 ? '' : 's'} — OVAL owes this`}
                    tone={m.rcmLiability > 0 ? 'warn' : 'default'}
                    icon={<Landmark className="h-5 w-5" />} />
            )}
            {(isProc || isBoard || isPm || isAdmin) && m.variedBy !== 0 && (
              <Stat label="Added by variation" value={rupees(m.variedBy)}
                    sub="on top of the signed contract values"
                    tone={m.variedBy > 0 ? 'warn' : 'good'}
                    icon={<ListChecks className="h-5 w-5" />} />
            )}
            {(isProc || isBoard || isPm || isAdmin)
              && (m.variationsInChain > 0 || m.variationDrafts > 0) && (
              <Stat label="Variations in progress" value={m.variationsInChain}
                    sub={m.variationDrafts
                      ? `${m.variationDrafts} still being written`
                      : 'awaiting an approver'}
                    tone={m.variationsInChain ? 'warn' : 'default'}
                    icon={<ListChecks className="h-5 w-5" />} />
            )}
            {(isProc || isBoard || isAdmin) && (
              <Stat label="Past completion date" value={m.overdue}
                    sub={m.overdue ? 'contracts running late' : 'all on programme'}
                    tone={m.overdue ? 'bad' : 'good'}
                    icon={<AlertTriangle className="h-5 w-5" />} />
            )}
          </div>

          {/* ---------- a thing that needs somebody's attention ---------- */}
          {m.noPan > 0 && (isProc || isAccounts || isAdmin) && (
            <div className="rounded-lg border border-amber-300 border-l-4 border-l-amber-600 bg-amber-50 px-4 py-3">
              <p className="text-sm font-semibold text-amber-900">
                <AlertTriangle className="mr-1 inline h-4 w-4" />
                {m.noPan} contractor{m.noPan === 1 ? ' has' : 's have'} no PAN on file
              </p>
              <p className="mt-0.5 text-xs text-amber-800">
                Without a PAN the TDS rate is 20% instead of 1% or 2%, and the
                deduction is the contractor&rsquo;s money. Add it on the vendor master
                before the first bill.
              </p>
            </div>
          )}

          {/* ---------- progress, the one chart everybody asked for ---------- */}
          <Card title="How far along each contract is"
                subtitle="Certified quantity against ordered quantity, line by line. Only lines with something ordered appear.">
            {prog.length === 0 && (
              <p className="py-6 text-center text-sm text-slate-500">
                Nothing measured yet.
              </p>
            )}
            <div className="space-y-3">
              {prog.filter((p) => n(p.qty_allowed) > 0)
                   .sort((a, b) => n(b.value_ready_to_bill) - n(a.value_ready_to_bill))
                   .slice(0, 12)
                   .map((p) => {
                const pct = Math.min(n(p.pct_complete), 100)
                return (
                  <div key={p.wo_line_id}>
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
                      <p className="min-w-0 flex-1 truncate text-sm font-medium text-slate-800">
                        {p.description}
                      </p>
                      <p className="shrink-0 text-xs tabular-nums text-slate-500">
                        {n(p.qty_certified).toFixed(3)} / {n(p.qty_allowed).toFixed(3)} {p.uom}
                        <span className="ml-2 font-semibold text-slate-700">{pct.toFixed(1)}%</span>
                      </p>
                    </div>
                    <p className="truncate text-[11px] text-slate-400">
                      {p.site_name} · {p.wo_title}
                    </p>
                    <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                      <div className={pct >= 100 ? 'h-full rounded-full bg-emerald-500'
                                                 : 'h-full rounded-full bg-amber-500'}
                           style={{ width: `${Math.max(pct, 1)}%` }} />
                    </div>
                  </div>
                )
              })}
            </div>
          </Card>

          {/* ---------- the list, so the tiles are not a dead end ---------- */}
          <Card title="Work orders"
                subtitle="Newest first"
                actions={<Link to="/service/work-orders"
                               className="inline-flex items-center gap-1 text-sm font-semibold text-amber-700 hover:underline">
                           See all <ArrowRight className="h-4 w-4" />
                         </Link>}>
            <div className="overflow-x-auto">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Work order</th><th>Site</th><th>Contractor</th>
                    <th className="num">Value</th><th>Status</th><th>Due</th>
                  </tr>
                </thead>
                <tbody>
                  {wos.slice(0, 8).map((w) => (
                    <tr key={w.id}>
                      <td className="whitespace-nowrap font-medium">
                        <Link to={`/service/work-orders/${w.id}`} className="text-amber-700 hover:underline">
                          {w.id}
                        </Link>
                        <span className="block max-w-[220px] truncate text-[11px] text-slate-400">
                          {w.title}
                        </span>
                      </td>
                      <td className="max-w-[160px] truncate">{w.site_name}</td>
                      <td className="max-w-[180px] truncate">
                        {w.vendor_name}
                        {w.is_reverse_charge && (
                          <span className="pill-grey ml-1" title="Unregistered — OVAL owes the GST">RCM</span>
                        )}
                      </td>
                      <td className="num whitespace-nowrap">{rupees(w.total_with_gst)}</td>
                      <td><StatusPill status={w.status} /></td>
                      <td className="whitespace-nowrap text-xs">
                        {w.completion_date ? ddmmyyyy(w.completion_date) : '—'}
                        {n(w.days_overdue) > 0 && (
                          <span className="ml-1 font-semibold text-red-600">
                            +{w.days_overdue}d
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* ---------- what Accounts and the board get that nobody else does ---------- */}
          {(isAccounts || isBoard || isAdmin) && (
            <Card title="Where the statutory position stands"
                  subtitle="Fuller registers arrive with RA bills in the next phase. These figures come from the work orders themselves.">
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Reverse charge GST
                  </p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-slate-800">
                    {rupees(m.rcmLiability)}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    18% on {m.rcmCount} unregistered contractor{m.rcmCount === 1 ? '' : 's'}.
                    Payable by OVAL, not deducted from them.
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    TDS basis
                  </p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-slate-800">
                    s.393(1)
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    1% individual or HUF, 2% otherwise — read off each contractor&rsquo;s PAN.
                    Replaced s.194C on 01-04-2026.
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Certified, not invoiced
                  </p>
                  <p className="mt-1 text-lg font-bold tabular-nums text-slate-800">
                    {rupees(m.readyToBill)}
                  </p>
                  <p className="mt-0.5 text-[11px] text-slate-500">
                    Work signed off at site with no bill raised. The accrual.
                  </p>
                </div>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-slate-500">
                <TrendingUp className="h-3.5 w-3.5" />
                Retention held, advances outstanding and the input-credit register
                arrive with RA Bills.
              </p>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
